import React, {useLayoutEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import {Portal} from '../../../util';
import filtersStore from '../filters/filters-store/filters-store';
import priceRangeStore from '../price-range/price-range-store';
import {clearActiveFilters, removeActiveFilter} from './active-filter-actions';
import {focusAfterActiveFiltersCleared, focusTargetAfterRemoval, type ChipFocusTarget} from './focus-after-chip-removal';
import {ACTIVE_FILTER_FRAME_TYPE_LABELS, listActiveFilterChips, schoolFilterNoun, type ActiveFilterChip} from './list-active-filter-chips';
import {ActiveFilterSummaryView} from './active-filter-summary-view';
import './active-filter-summary.scss';

export const ActiveFilterSummary: React.FC = observer(() => {
    const chips = listActiveFilterChips({
        schoolNoun: schoolFilterNoun(Portal?.PortalID),
        appliedSchool: filtersStore.filters.inst,
        priceMin: filtersStore.filters.priceMin,
        priceMax: filtersStore.filters.priceMax,
        defaultPriceMin: priceRangeStore.min,
        defaultPriceMax: priceRangeStore.max,
        mouldings: filtersStore.selectedFinish,
        mats: filtersStore.selectedMatCombo,
        frameTypes: filtersStore.selectedFrameType,
        baselineFrameTypes: filtersStore.getBaselineFrameTypes(),
        graduationYear: filtersStore.filters.graduationYear,
        frameTypeLabels: ACTIVE_FILTER_FRAME_TYPE_LABELS,
    });
    const pendingFocus = useRef<ChipFocusTarget | null>(null);
    const [focusNonce, setFocusNonce] = useState(0);
    const removeButtonRefs = useRef(new Map<string, HTMLButtonElement>());

    useLayoutEffect(() => {
        const target = pendingFocus.current;
        if (!target) {
            return;
        }

        pendingFocus.current = null;

        if (target.type === 'chip') {
            const button = removeButtonRefs.current.get(target.id);
            if (button?.isConnected) {
                button.focus();
                return;
            }
        }

        focusAfterActiveFiltersCleared();
    }, [focusNonce]);

    const removeButtonRef = (id: string, element: HTMLButtonElement | null) => {
        if (element) {
            removeButtonRefs.current.set(id, element);
            return;
        }

        removeButtonRefs.current.delete(id);
    };

    const onRemove = (chip: ActiveFilterChip, index: number) => {
        const remainingIds = chips.filter(item => item.id !== chip.id).map(item => item.id);
        pendingFocus.current = focusTargetAfterRemoval(index, remainingIds);
        setFocusNonce(nonce => nonce + 1);
        void removeActiveFilter(chip);
    };

    const onClearAll = () => {
        pendingFocus.current = {type: 'cleared'};
        setFocusNonce(nonce => nonce + 1);
        void clearActiveFilters();
    };

    return (
        <ActiveFilterSummaryView
            chips={chips}
            onRemove={onRemove}
            onClearAll={onClearAll}
            removeButtonRef={removeButtonRef}
        />
    );
});
